import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import { ERROR_MESSAGES } from "../../../constants/enums";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import {
  ICommAutoBinder,
  ICommAutoChecklistResponse,
  ICommAutoPolicyMarketingBinderPdf,
  ICommAutomobileMarketingHistory,
} from "./commAutoMarketingHistory.types";
import { ITemplateField } from "../../CustomFormFields/CustomFormFields.types";
import { IFileUpload } from "../../../features/FileUpload/components/FileUpload.type";
import { getUniqueId } from "../../../helpers";
import { uploadMultipleFile } from "../../../features/FileUpload/utils";
import { IMail } from "../../common/common.types";
import { IInsurerUnderWriter } from "../../insurer/insurer.types";
import { Buffer } from "buffer";
import { buildBodyKey, buildListKey } from "../leadKeying";
import { normalizeEmail, normalizeEmailList } from "../../../helpers/email";

export const commAutoPolicyPreviewMarketingAsync =
  (
    data: ICommAutoPolicyMarketingBinderPdf,
    onCallback: (isSuccess: boolean, html?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.post(`/policy/market-binder-preview-or-pdf`, data);

      onCallback(true, res.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const commAutoPolicyGenerateMarketCoveragePdfAsync =
  (
    data: ICommAutoPolicyMarketingBinderPdf,
    onCallback: (isSuccess: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(`/policy/market-binder-preview-or-pdf`, data);

      onCallback(true, res?.data);
      //dispatch(fetchBusinessMarketingAttachmentsAsync(data));
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
    finally {
      dispatch(saveLoaderCompleted());
    }
  };

/**
 * Marketing-submission PDF generation.
 *
 * The `/policy/market-binder-preview-or-pdf` endpoint, when called for a market
 * submission (not the binder-pdf mode), responds with the generated PDF as a
 * raw binary buffer rather than an array of saved file paths. We request it as
 * an arraybuffer, wrap it into a PDF `File`, and hand back a ready-to-upload
 * `IFileUpload` so the Marketing dialog can drop it straight into its
 * Attachments list. The file is uploaded + attached to the marketing-history
 * record on final submit via `uploadMultipleFile`.
 */
export const commAutoPolicyGenerateMarketSubmissionPdfFileAsync =
  (
    data: ICommAutoPolicyMarketingBinderPdf,
    onCallback: (isSuccess: boolean, file?: IFileUpload) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(`/policy/market-binder-preview-or-pdf`, data, {
        responseType: "arraybuffer",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const fileName = `Marketing_Submission_${data.customer_policy_id ?? ""}_${Date.now()}.pdf`;
      const file = new File([blob], fileName, { type: "application/pdf" });

      const attachment: IFileUpload = {
        key: getUniqueId(),
        file,
        path: null,
        name: fileName,
      };

      onCallback(true, attachment);
    } catch (err: any) {
      // With responseType "arraybuffer" an error body also comes back as a
      // buffer, so decode it before surfacing the message.
      let message = "Failed to generate the marketing submission PDF.";
      try {
        if (err?.response?.data) {
          const decoded = JSON.parse(
            new TextDecoder().decode(err.response.data)
          );
          message = decoded?.message ?? message;
        }
      } catch {
        // keep the default message
      }
      dispatch(
        showMessage({
          type: "error",
          message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const commAutoPolicyBinderSendCoverageMailAndHistAsync =
  (
    data: ICommAutoPolicyMarketingBinderPdf,
    keys: Array<string>,
    bccEmail: string,
    subject: string,
    text: string,
    customerMail: string,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const bufferJson = await api.post("/general/download-files", {
        type: "json",
        keys,
      });
      const mailPayload: IMail = {
        from_email: null,
        from_name: null,
        emails: normalizeEmailList([customerMail]),
        bcc: bccEmail ? [normalizeEmail(bccEmail)] : [],
        cc: [],
        subject: subject,
        body: text,
        templateName: null,
        templateId: null,
        objectVariables: {},
        attachments: bufferJson?.data?.map((ele: any, index: number) => ({
          content: ele,
          filename: keys[index].split("/").at(-1),
        })),
      };

      const historyPayload: ICommAutomobileMarketingHistory[] = [];

      // mailPayload.attachments = attachmentResponse.data;
      // const a = Buffer.from(attachmentResponse.data);
      // console.log(a);

      await api.post("/general/send-email", mailPayload);

      for (const payload of historyPayload) {
        // await api.post(`/policy/edit-policy-commauto-add-marketing-history`, payload);
      }
      dispatch(
        showMessage({
          type: "success",
          message: "Email and Marketing history saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const commAutoPolicySendCoverageMailAndHistAsync =
  (
    data: ICommAutoPolicyMarketingBinderPdf,
    attachments: IFileUpload[],
    bccEmail: string,
    subject: string,
    text: string,
    underWriters: IInsurerUnderWriter[],
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const mailAttachments: { content: any; filename: string }[] = [];

      // Handle server-stored files (have path but no file)
      const serverKeys = attachments
        .filter((a) => a.path && !a.file)
        .map((a) => a.path as string);

      if (serverKeys.length > 0) {
        const bufferJson = await api.post("/general/download-files", {
          type: "json",
          keys: serverKeys,
        });
        bufferJson?.data?.forEach((ele: any, index: number) => {
          mailAttachments.push({
            content: ele,
            filename: serverKeys[index].split("/").at(-1) || "attachment",
          });
        });
      }

      // Handle locally uploaded files (have file object)
      const localFiles = attachments.filter((a) => a.file);
      for (const item of localFiles) {
        if (item.file) {
          const arrayBuffer = await item.file.arrayBuffer();
          const buffer = Buffer.from(arrayBuffer);
          mailAttachments.push({
            content: buffer as any,
            filename: item.name || item.file.name,
          });
        }
      }

      const mailPayload: IMail = {
        from_email: null,
        from_name: null,
        emails: [],
        bcc: bccEmail ? [normalizeEmail(bccEmail)] : [],
        cc: [],
        templateId: null,
        subject: subject,
        body: text,
        templateName: null,
        objectVariables: {},
        attachments: mailAttachments,
      };

      const historyPayload: ICommAutomobileMarketingHistory[] = [];

      // Upload / resolve the attachment paths once so every history record points
      // at the same stored files. Binder-generated files already carry a server
      // `path` (so uploadMultipleFile short-circuits for them), while locally added
      // files get uploaded and their returned paths are used. Without this the
      // generated document is only emailed and never attached to the saved
      // marketing-history record (first-step "save" not auto-attaching).
      const { paths: attachmentPaths } = await uploadMultipleFile(
        attachments,
        "POLICY",
        {
          customer_id: data.customer_id ?? null,
          policy_type: "COMMAUTO",
          customer_policy_id: data.customer_policy_id,
          page_name: "Marketing",
        }
      );
      const historyAttachments = attachmentPaths.filter(
        (path): path is string => Boolean(path)
      );

      for (const item of underWriters) {
        mailPayload.emails.push(normalizeEmail(item.email));
        historyPayload.push({
          customer_policy_id: data.customer_policy_id,
          quote_number: null,
          serial_number: null,
          premium: null,
          date_received: null,
          // Backend stores attachment as an array of string paths; the shared
          // type models it as IFileUpload[] so cast to keep runtime behavior
          // consistent with addCommAutomobilePolicyMarketingHistAsync.
          attachment: historyAttachments as unknown as IFileUpload[],
          notes: null,
          status: "ACTIVE",
          underwriter: item.name,
          insurer: item.insurer_code,
          submission_date: item.insert_ts,
          effective_date: null,
          quote_status: "Submitted",
          customer_name: "",
          assigned_to_id: null,
          assigned_to_name: null,
        });
      }
      await api.post("/general/send-email", mailPayload);

      // Persist a marketing-history record for each selected underwriter. This is
      // what records the submission against the policy; without it only the email
      // and file-download calls fire and no marketing submission is saved.
      for (const payload of historyPayload) {
        await api.post(
          `/policy/edit-policy-commauto-add-marketing-history`,
          payload
        );
      }

      // Re-fetch the marketing-history list so the newly saved submission shows
      // up in the UI without a manual refresh.
      dispatch(
        fetchCommAutomobilePolicyMarketingHistListAsync(data.customer_policy_id)
      );

      dispatch(
        showMessage({
          type: "success",
          message: "Email and Marketing history saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_LIST_PROGRESS =
  "FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_LIST_PROGRESS";
export const FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_LIST_SUCCESS =
  "FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_LIST_SUCCESS";
export const FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_LIST_FAILED =
  "FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_LIST_FAILED";

export const fetchCommAutomobilePolicyMarketingHistListProgress = () =>
  action(FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_LIST_PROGRESS);
export const fetchCommAutomobilePolicyMarketingHistListSuccess = (
  data: ICommAutomobileMarketingHistory[]
) =>
  action(FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_LIST_SUCCESS, { data });
export const fetchCommAutomobilePolicyMarketingHistListFailed = () =>
  action(FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_LIST_FAILED);

export const fetchCommAutomobilePolicyMarketingHistListAsync =
  (
    customerPolicyId: number,
    leadId?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchCommAutomobilePolicyMarketingHistListProgress());
      const res = await api.get(
        `/policy/get-policy-commauto-add-marketing-history?${buildListKey({
          customerPolicyId,
          leadId,
        })}`
      );
      const data: ICommAutomobileMarketingHistory[] = res.data.data;
      dispatch(fetchCommAutomobilePolicyMarketingHistListSuccess(data));
    } catch (err: any) {
      dispatch(fetchCommAutomobilePolicyMarketingHistListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addCommAutomobilePolicyMarketingHistAsync =
  (
    data: ICommAutomobileMarketingHistory,
    customerId: number,
    onCallback: (isSuccess: boolean) => void,
    leadId?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: customerId,
        policy_type: "COMMAUTO",
        customer_policy_id: data.customer_policy_id,
        page_name: "Marketing",
      };
      const { paths } = await uploadMultipleFile(
        data.attachment,
        "POLICY",
        asPayload
      );
      const { paths: quotePaths } = await uploadMultipleFile(
        data.quote_attachment ?? [],
        "POLICY",
        asPayload
      );
      await api.post(
        `/policy/edit-policy-commauto-add-marketing-history`,
        buildBodyKey(
          { ...data, attachment: paths, quote_attachment: quotePaths },
          leadId
        )
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Marketing History saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_PROGRESS =
  "FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_PROGRESS";
export const FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_SUCCESS =
  "FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_SUCCESS";
export const FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_FAILED =
  "FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_FAILED";

export const fetchCommAutomobilePolicyMarketingHistProgress = () =>
  action(FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_PROGRESS);
export const fetchCommAutomobilePolicyMarketingHistSuccess = (
  data: ICommAutomobileMarketingHistory
) => action(FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_SUCCESS, { data });
export const fetchCommAutomobilePolicyMarketingHistFailed = (
  errorMessage: string
) =>
  action(FETCH_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY_FAILED, {
    errorMessage,
  });

export const fetchCommAutomobilePolicyMarketingHistAsync =
  (
    customerPolicyId: number,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchCommAutomobilePolicyMarketingHistProgress());
      const res = await api.get(
        `/policy/get-policy-commauto-add-marketing-history?customer_policy_id=${customerPolicyId}&column=serial_number&value=${incrementNumber}`
      );
      const data: ICommAutomobileMarketingHistory[] = res.data.data;
      if (data.length > 0) {
        const attachments: IFileUpload[] = [];
        for (const item of data[0].attachment as any) {
          attachments.push({
            file: null,
            key: getUniqueId(),
            path: item,
          });
        }
        const rawQuote = data[0].quote_attachment as any;
        const quoteAttachments: IFileUpload[] = (
          Array.isArray(rawQuote) ? rawQuote : rawQuote ? [rawQuote] : []
        ).map((item: string) => ({
          file: null,
          key: getUniqueId(),
          path: item,
        }));
        dispatch(
          fetchCommAutomobilePolicyMarketingHistSuccess({
            ...data[0],
            attachment: attachments,
            quote_attachment: quoteAttachments,
          })
        );
      } else {
        dispatch(
          fetchCommAutomobilePolicyMarketingHistFailed(
            ERROR_MESSAGES.POLICY_NOT_FOUND
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchCommAutomobilePolicyMarketingHistFailed(
          ERROR_MESSAGES.SERVER_ERROR
        )
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_COMM_AUTO_CHECKLIST_SAVED_STATUS_PROGRESS =
  "FETCH_COMM_AUTO_CHECKLIST_SAVED_STATUS_PROGRESS";
export const FETCH_COMM_AUTO_CHECKLIST_SAVED_STATUS_SUCCESS =
  "FETCH_COMM_AUTO_CHECKLIST_SAVED_STATUS_SUCCESS";
export const FETCH_COMM_AUTO_CHECKLIST_SAVED_STATUS_FAILED =
  "FETCH_COMM_AUTO_CHECKLIST_SAVED_STATUS_FAILED";
export const SAVE_COMM_AUTO_CHECKLIST_RESPONSE_PROGRESS =
  "SAVE_COMM_AUTO_CHECKLIST_RESPONSE_PROGRESS";
export const SAVE_COMM_AUTO_CHECKLIST_RESPONSE_SUCCESS =
  "SAVE_COMM_AUTO_CHECKLIST_RESPONSE_SUCCESS";
export const SAVE_COMM_AUTO_CHECKLIST_RESPONSE_FAILED =
  "SAVE_COMM_AUTO_CHECKLIST_RESPONSE_FAILED";

export const fetchCommAutoChecklistSavedStatusProgress = () =>
  action(FETCH_COMM_AUTO_CHECKLIST_SAVED_STATUS_PROGRESS);
export const fetchCommAutoChecklistSavedStatusSuccess = (
  saved: boolean,
  response: ICommAutoChecklistResponse | null
) =>
  action(FETCH_COMM_AUTO_CHECKLIST_SAVED_STATUS_SUCCESS, { saved, response });
export const fetchCommAutoChecklistSavedStatusFailed = () =>
  action(FETCH_COMM_AUTO_CHECKLIST_SAVED_STATUS_FAILED);
export const saveCommAutoChecklistResponseProgress = () =>
  action(SAVE_COMM_AUTO_CHECKLIST_RESPONSE_PROGRESS);
export const saveCommAutoChecklistResponseSuccess = (
  response: ICommAutoChecklistResponse
) => action(SAVE_COMM_AUTO_CHECKLIST_RESPONSE_SUCCESS, { response });
export const saveCommAutoChecklistResponseFailed = (errorMessage: string) =>
  action(SAVE_COMM_AUTO_CHECKLIST_RESPONSE_FAILED, { errorMessage });

export const fetchCommAutoChecklistSavedStatusAsync =
  (
    customerPolicyId: number | string,
    onComplete?: (fields: ITemplateField[] | null) => void,
    leadId?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchCommAutoChecklistSavedStatusProgress());
      const res = await api.get(
        `/policy/get-policy-binder-checklist?${buildListKey({
          customerPolicyId: customerPolicyId as number,
          leadId,
        })}`
      );
      // API returns { data: [...] } — take the first record
      const list: ICommAutoChecklistResponse[] = res.data.data ?? [];
      const record = list.length > 0 ? list[0] : null;
      if (record !== null) {
        dispatch(fetchCommAutoChecklistSavedStatusSuccess(true, record));
        onComplete?.(record.checklist ?? null);
      } else {
        dispatch(fetchCommAutoChecklistSavedStatusSuccess(false, null));
        onComplete?.(null);
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        dispatch(fetchCommAutoChecklistSavedStatusSuccess(false, null));
        onComplete?.(null);
      } else {
        dispatch(fetchCommAutoChecklistSavedStatusFailed());
        onComplete?.(null);
      }
    }
  };

export const saveCommAutoChecklistResponseAsync =
  (
    customerId: number | string,
    customerPolicyId: number | string,
    fields: ITemplateField[],
    onSuccess: () => void,
    onError: (message: string) => void,
    leadId?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveCommAutoChecklistResponseProgress());
      // Send binder_checklist_code: null on first save (INSERT),
      // actual code on subsequent saves so backend performs UPDATE.
      const existingCode =
        getState().commAuto.marketing.checklist.response
          ?.binder_checklist_code ?? null;
      const res = await api.post(
        `/policy/edit-policy-binder-checklist`,
        buildBodyKey(
          {
            binder_checklist_code: existingCode,
            customer_id: customerId,
            customer_policy_id: customerPolicyId,
            checklist: fields,
            status: "ACTIVE",
          },
          leadId
        )
      );
      // Save response may also be an array — normalise to single record
      const responseData = res.data.data;
      const record = Array.isArray(responseData)
        ? responseData[0] ?? null
        : responseData ?? null;
      dispatch(saveCommAutoChecklistResponseSuccess(record));
      onSuccess();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ?? "Failed to save checklist response.";
      dispatch(saveCommAutoChecklistResponseFailed(message));
      onError(message);
    }
  };

export const CLEAR_COMM_AUTOMOBILE_POLICY_MARKETING_STATE =
  "CLEAR_COMM_AUTOMOBILE_POLICY_MARKETING_STATE";
export const CLEAR_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY =
  "CLEAR_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY";

export const clearCommAutonmobilePolicyMarketingState = () =>
  action(CLEAR_COMM_AUTOMOBILE_POLICY_MARKETING_STATE);
export const clearCommAutomobilePolicyMarketingHistory = () =>
  action(CLEAR_COMM_AUTOMOBILE_POLICY_MARKETING_HISTORY);

export const FETCH_COMMAUTO_POLICY_BINDER_LIST_PROGRESS =
  "FETCH_COMMAUTO_POLICY_BINDER_LIST_PROGRESS";
export const FETCH_COMMAUTO_POLICY_BINDER_LIST_SUCCESS =
  "FETCH_COMMAUTO_POLICY_BINDER_LIST_SUCCESS";
export const FETCH_COMMAUTO_POLICY_BINDER_LIST_FAILED =
  "FETCH_COMMAUTO_POLICY_BINDER_LIST_FAILED";

export const fetchCommAutoPolicyBindertListProgress = () =>
  action(FETCH_COMMAUTO_POLICY_BINDER_LIST_PROGRESS);
export const fetchCommAutoPolicyBindertListSuccess = (
  data: ICommAutoBinder[]
) => action(FETCH_COMMAUTO_POLICY_BINDER_LIST_SUCCESS, { data });
export const fetchCommAutoPolicyBindertListFailed = () =>
  action(FETCH_COMMAUTO_POLICY_BINDER_LIST_FAILED);

export const fetchCommAutoPolicyBinderistListAsync =
  (
    customerPolicyId: number,
    leadId?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchCommAutoPolicyBindertListProgress());
      const res = await api.get(
        `/policy/get-policy-commauto-binder-details?${buildListKey({
          customerPolicyId,
          leadId,
        })}`
      );
      const data: ICommAutoBinder[] = res.data.data;
      dispatch(fetchCommAutoPolicyBindertListSuccess(data));
    } catch (err: any) {
      dispatch(fetchCommAutoPolicyBindertListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_COMM_AUTO_POLICY_MARKETING_STATE =
  "CLEAR_COMM_AUTO_POLICY_MARKETING_STATE";
export const CLEAR_COMM_AUTO_POLICY_MARKETING_HISTORY =
  "CLEAR_COMM_AUTO_POLICY_MARKETING_HISTORY";

export const clearCommAutoPolicyMarketingState = () =>
  action(CLEAR_COMM_AUTO_POLICY_MARKETING_STATE);
export const clearCommAutoPolicyMarketingHistory = () =>
  action(CLEAR_COMM_AUTO_POLICY_MARKETING_HISTORY);

export const CLEAR_COMM_AUTO_POLICY_MARKETING_LIST_STATE =
  "CLEAR_COMM_AUTO_POLICY_MARKETING_LIST_STATE";
export const clearCommAutoPolicyMarketingListState = () =>
  action(CLEAR_COMM_AUTO_POLICY_MARKETING_LIST_STATE);

export const CLEAR_COMM_AUTO_POLICY_MARKETING_FORM_STATE =
  "CLEAR_COMM_AUTO_POLICY_MARKETING_FORM_STATE";
export const clearCommAutoPolicyMarketingFormState = () =>
  action(CLEAR_COMM_AUTO_POLICY_MARKETING_FORM_STATE);
